<template>
	<section class="b360-grid-row">
		<form-dropdowns
			class="b360-col-xs-12 b360-col-sm-12 b360-col-md-12 b360-col-lg-6"
			:label="'Target Rate Drop (includes your comp plan)'"
			:disabled="disabled"
			:options="rateDropOptions"
			:name="rateWatch.name"
			:value="rateWatch.parameters.RatePercentageIncrements"
			:required="true"
			@change="
				(e) => {
					if (!disabled)
						this.updateAlertValue({
							preferenceIndex: alertIndex,
							key: 'parameters.RatePercentageIncrements',
							value: e.value,
						});
				}
			"
		></form-dropdowns>
		<label class="b360-form-label b360-col-12"
			>Please Select Target Rate (%) or Target Price ($) for the credit toward
			borrower cost
			<font-awesome-icon
				class="tooltip-target b360-form-icon"
				:icon="['fal', 'question-circle']"
				v-tooltip="{
					content: 'select either a percentage or dollar amount to be used',
					trigger: 'hover click',
					placement: 'top-end',
				}"
		/></label>

		<!-- Percent or Dollar Pricing -->
		<div class="b360-col-6 pricing-group">
			<FormRadioButtons
				:radioGroup="[
					{
						name: 'rateWatchPricing',
						label: 'Target Rate (%)',
						value: 0,
						id: 0,
						section: 'rw',
						checked: isPercent,
					},
				]"
				@handle-input="handleRateWatchPricing(true)"
				:isRateWatchPricing="true"
				:disabled="disabled"
			></FormRadioButtons>
			<FormInputs
				:name="'percentPrice'"
				:disabled="!isPercent || disabled"
				:value="isPercent ? ratePricing : 0"
				:key="isPercent"
				:required="isPercent"
				:type="'number'"
				:step="0.01"
				@handle-input="
					(emitObj) => {
						if (!disabled)
							updateAlertValue({
								preferenceIndex: alertIndex,
								key: 'parameters.RatePricing',
								value: emitObj.value,
							});
					}
				"
			></FormInputs>
		</div>

		<div class="b360-col-6 pricing-group">
			<FormRadioButtons
				:radioGroup="[
					{
						name: 'rateWatchPricing',
						label: 'Target Price ($)',
						value: 1,
						id: 1,
						section: 'rw',
						checked: !isPercent,
					},
				]"
				@handle-input="!disabled ? handleRateWatchPricing(false) : ''"
				:isRateWatchPricing="true"
				:disabled="disabled"
			></FormRadioButtons>
			<FormInputs
				:name="'dollarPrice'"
				:disabled="isPercent || disabled"
				:value="!isPercent ? ratePricing : 0"
				:type="'number'"
				:step="0.01"
				:key="isPercent"
				:required="!isPercent"
				@handle-input="
					(emitObj) => {
						if (!disabled)
							updateAlertValue({
								preferenceIndex: alertIndex,
								key: 'parameters.RatePricing',
								value: emitObj.value,
							});
					}
				"
			></FormInputs>
		</div>
		<!-- end Percent or Dollar Pricing -->

		<ul class="b360-col-12 pricing-info">
			<li>This loan includes LLPA and the same product type is used.</li>

			<li v-if="rateWatch.parameters.IsTargetAfterComp">
				You selected a
				<span v-show="!isPercent">$</span>{{ ratePricing
				}}<span v-show="isPercent">%</span> credit toward borrower cost.
			</li>

			<li v-else>
				You selected a <span v-show="!isPercent">$</span>{{ ratePricing
				}}<span v-show="isPercent">%</span> credit toward borrower cost.
			</li>
		</ul>
	</section>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import FormRadioButtons from '@/components/global/FormRadioButtons.vue';
import FormCheckboxButtons from '@/components/global/FormCheckboxButtons.vue';
import FormInputs from '@/components/global/FormInputs.vue';
import FormDropdowns from '@/components/global/FormDropdowns.vue';
import Checkbox from '@/components/global/CheckBox.vue';
import apiClient from '../../services/api-client';

export default {
	name: 'AlertRateWatchLayout',
	components: {
		Checkbox,
		FormRadioButtons,
		FormCheckboxButtons,
		FormInputs,
		FormDropdowns,
	},
	props: {
		disabled: Boolean,
		rateWatch: Object,
		alertIndex: Number,
	},
	data() {
		return {
			rateDropOptions: [],
			compPlan: {},
		};
	},
	created() {
		this.hydrateRateWatchOptions();
		this.hydrateCompPlan();
	},
	computed: {
		...mapState({
			isTargetAfterComp: (state) => {
				const index = state.userAlerts.preferences
					.map((x) => x.name)
					.indexOf('RateWatch');
				const targetAfterComp =
					state.userAlerts.preferences[index].parameters.IsTargetAfterComp;
				return targetAfterComp === true || targetAfterComp === 'true';
			},
			isPercent: (state) => {
				const index = state.userAlerts.preferences
					.map((x) => x.name)
					.indexOf('RateWatch');
				const isPercent =
					state.userAlerts.preferences[index].parameters.IsRatePricingPercent;
				return isPercent === true || isPercent === 'true';
			},
			ratePricing: (state) => {
				const index = state.userAlerts.preferences
					.map((x) => x.name)
					.indexOf('RateWatch');
				return state.userAlerts.preferences[index].parameters.RatePricing;
			},
		}),
	},
	methods: {
		...mapActions([
			'selectImage',
			'updateAlertValue',
			'updateEmailTextValues',
			'getRateDropOptions',
		]),
		handleRateWatchPricing(isPercent) {
			// if the value is true that means that the percent radio was selected. so IsRatePricingPercent should be true
			if (isPercent === true) {
				this.updateAlertValue({
					preferenceIndex: this.alertIndex,
					key: 'parameters.IsRatePricingPercent',
					value: 'true',
				});
				this.updateAlertValue({
					preferenceIndex: this.alertIndex,
					key: 'parameters.RatePricing',
					value: '0',
				});
			} else {
				this.updateAlertValue({
					preferenceIndex: this.alertIndex,
					key: 'parameters.IsRatePricingPercent',
					value: 'false',
				});
				this.updateAlertValue({
					preferenceIndex: this.alertIndex,
					key: 'parameters.RatePricing',
					value: '0',
				});
			}
		},
		isChecked(index) {
			return index === this.selectedIndex;
		},
		async hydrateRateWatchOptions() {
			this.rateDropOptions = await apiClient()
				.post('/PreferenceCenter/GetAllPreferenceParameterValues')
				.then((response) => {
					const arr = [];
					response.data[0].parameters.RatePercentageIncrements.forEach(
						(el, index) => {
							arr[index] = {
								id: index + 1,
								value: el,
								name: el + '%',
							};
						},
					);
					return arr;
				});
		},
		async hydrateCompPlan() {
			this.compPlan = await apiClient()
				.post('/Profile/GetEntityCompensationPlan', {
					roledexEntityId: this.$store.state.companyProfile.roledexEntityId,
				})
				.then((response) => {
					return response.data;
				});
		},
	},
};
</script>

<style lang="scss" scoped>
.alert-layout-content {
	.target-comp {
		&--label {
			@include rem('margin-left', 10px);
			display: inline;
		}

		// running into some specificity issues
		&--checkbox#target-comp-checkbox {
			&.checked,
			&:hover {
				background-color: $brand-blue;
			}
		}
	}

	.pricing-group fieldset {
		border: none;
		margin-bottom: 0;

		.radio-button {
			margin-bottom: 0;

			label[for*='pricing'] {
				font-weight: 500;
				margin-bottom: 0;
			}
		}
	}
}

.pricing-info {
	@include rem('padding-left', 10px);
	list-style-type: none;

	li {
		@include rem('padding-left', 5px);
		position: relative;

		&::before {
			@include rem('height', 10px);
			@include rem('width', 10px);
			@include rem('top', 5px);
			content: '';
			position: absolute;
			right: 100%;
			border-radius: 50%;
			background: $border-color-light;
		}
	}
}
</style>
